import { useState } from "react";
import { openMailto } from "@/utils/mailto";
import Help from "@features/business/components/help";
import Button from "@features/business/components/button";
import InputField from "@features/business/components/inputField";
import documentIcon from "@assets/icons/document.svg";
import downloadIcon from "@assets/icons/download-arrow.svg";
import arrowSmallIcon from "@assets/icons/arrow-up-s-fill.svg";
import uploadIcon from "@assets/icons/upload.svg";
import { resourceLibraryFolders } from "@features/business/data";

interface DownloadResourcesPageProps {
  hero: React.ReactNode;
  footer?: React.ReactNode;
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function DownloadResourcesPage({
  hero,
  footer,
}: DownloadResourcesPageProps) {
  return (
    <div className="w-full flex flex-col bg-[#F1F1F1]">
      {hero}

      {/* ── RESOURCE LIBRARY ACCORDION ──────────────────────────────────── */}
      <section className="pt-10 md:pt-[60px] pb-10 md:pb-20 px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto space-y-3 md:space-y-6">
          {resourceLibraryFolders.map((folder, index) => (
            <List
              key={index}
              title={folder.title}
              folderLabel={`File ${index + 1}`}
              index={index + 1}
              isDefaultOpen={index < 2}
            >
              <div className="grid gap-3 md:gap-6 md:grid-cols-2">
                {folder.documents.map((doc, di) => (
                  <div
                    key={di}
                    className="flex items-center justify-between gap-4 p-3 md:p-4 rounded-[12px] hover:bg-[#F9F9F9] border border-[#62636C33]"
                  >
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="bg-[#F1F1F1] p-1.5 md:p-2 rounded-[10px] flex-shrink-0">
                        <img
                          src={documentIcon}
                          alt="document"
                          className="size-5 md:size-8"
                        />
                      </div>
                      <div>
                        <p className="text-[#090D2C] font-medium text-xs md:text-sm lg:text-lg leading-snug">
                          {doc.title}
                        </p>
                        <p className="mt-0.5 text-[14px] md:text-xs lg:text-base text-[#62636C]">
                          {doc.desc}
                        </p>
                      </div>
                    </div>
                    <button className="flex-shrink-0 size-7 md:size-9 hover:bg-[#F1F1F1] border border-[#F1F1F1] rounded-full grid place-content-center">
                      <img
                        src={downloadIcon}
                        alt="download"
                        className="size-4 md:size-5 invert"
                      />
                    </button>
                  </div>
                ))}
              </div>
            </List>
          ))}
        </div>
      </section>

      {/* ── DOCUMENT REQUEST FORM ────────────────────────────────────────── */}
      <section className="py-10 md:py-20 px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Document Request Form
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] md:mt-4">
            Investors or stakeholders may request official copies of documents.
          </p>

          <DocumentRequestForm />

          {/* Notes & Disclaimers */}
          <div className="mt-10 md:mt-16">
            <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl mb-4">
              Notes &amp; Disclaimers
            </h3>
            <div className="grid gap-2 md:grid-cols-2">
              {[
                { text: "All documents remain property of GAZ." },
                {
                  text: "Safety documents follow ",
                  bold: "DCD & ESMA standards.",
                },
                {
                  text: "Reproduction without written permission is prohibited.",
                },
                {
                  text: "Billing documents follow ",
                  bold: "FTA VAT Regulations.",
                },
              ].map((note, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                  <span className="text-[14px] md:text-sm lg:text-lg text-[#62636C]">
                    {note.text}
                    {note.bold && (
                      <strong className="text-[#090D2C]">{note.bold}</strong>
                    )}
                  </span>
                </li>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── HELP CTA ───────────────────────────────────────────────────── */}
      <Help />

      {footer}
    </div>
  );
}

const documentOptions = [
  "Select documents required",
  "Trade License",
  "DCD Compliance Certificates",
  "ESMA Conformity Certificates",
  "VAT Registration Certificate",
  "HSE Policy Manual",
];

const DocumentRequestForm = () => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    const fullName = formData.get("fullName");
    const email = formData.get("email");
    const accountNumber = formData.get("accountNumber");
    const documentRequired = formData.get("documentRequired");

    const body = `Hello,

      I would like to request access to the following document.

      Full Name: ${fullName}
      Email Address: ${email}
      Account Number: ${accountNumber}

      Document Requested:
      ${documentRequired}

      Thank you.`;

    openMailto({
      subject: `Document Request - ${documentRequired}`,
      body,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-6 md:mt-12 bg-white rounded-[12px] md:rounded-[24px] px-5 md:px-10 lg:px-[100px] py-5 md:py-10 lg:py-20 space-y-6 md:space-y-8 w-full"
    >
      <div className="flex flex-col gap-6 md:flex-row">
        <InputField
          label="Full Name"
          placeholder="Enter your full name"
          name="fullName"
          required
        />

        <InputField
          label="Email Address"
          placeholder="Enter your email address"
          type="email"
          name="email"
          required
        />
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        <InputField
          label="Account Number"
          placeholder="Enter your account number"
          name="accountNumber"
          required
        />

        <InputField
          label="Documents Required"
          placeholder="Select documents required"
          as="select"
          name="documentRequired"
          required
          options={documentOptions.map((data, index) => ({
            label: data,
            value: index === 0 ? "" : data,
          }))}
        />
      </div>

      <div>
        <label className="text-sm md:text-lg lg:text-xl text-[#0A0E2EBF] font-medium block mb-2 md:mb-3">
          Upload Authorization Letter (if required)
        </label>

        <div className="bg-[#F1F1F1] border border-[#62636C] border-dashed rounded-md md:rounded-lg h-[173px] w-full grid place-content-center relative">
          <input
            type="file"
            name="authorizationLetter"
            className="absolute inset-0 opacity-0 cursor-pointer"
          />

          <div>
            <img src={uploadIcon} alt="upload icon" className="mx-auto" />
            <span className="text-[#62636C] text-xs md:text-lg block text-center mt-6">
              Upload authorization letter
            </span>
          </div>
        </div>
      </div>

      <div className="flex justify-center pt-5">
        <Button title="Submit Request" type="submit" />
      </div>
    </form>
  );
};

// ─── Accordion List ───────────────────────────────────────────────────────────
type ListProps = {
  children: React.ReactNode;
  title: string;
  folderLabel: string;
  index: number;
  isDefaultOpen?: boolean;
};

const List = ({
  children,
  title,
  folderLabel,
  index,
  isDefaultOpen = false,
}: ListProps) => {
  const [open, setOpen] = useState(isDefaultOpen);

  return (
    <article className="p-3 md:p-6 bg-white rounded-[12px] md:rounded-[24px]">
      <div
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center justify-between gap-5 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="bg-black size-9 md:size-[56px] grid place-content-center rounded-[12px] flex-shrink-0">
            <span className="text-xs font-semibold text-white md:text-base lg:text-lg">
              {index >= 10 ? index : `0${index}`}
            </span>
          </div>
          <div>
            <p className="text-[10px] md:text-xs text-[#62636C]">
              {folderLabel}
            </p>
            <h3 className="text-[#1C1C1C] text-xs md:text-base lg:text-xl font-semibold">
              {title}
            </h3>
          </div>
        </div>
        <img
          src={arrowSmallIcon}
          alt="arrow icon"
          className={`flex-shrink-0 transition-transform duration-300 ${
            open ? "" : "rotate-180"
          }`}
        />
      </div>

      {/* GRID COLLAPSIBLE */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="border-[#B9BBC6] border-t pt-4 mt-4">{children}</div>
        </div>
      </div>
    </article>
  );
};
